<template>
  <div class="observaciones-planilla p-4">

    <!-- Header info -->
    <div class="mb-3">
      <p class="mb-0 text-muted">
        Trámite: <strong>{{ idTramite }}</strong> —
        Tipo: <strong>{{ tipoTexto }}</strong>
      </p>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <!-- Loading -->
    <div v-if="loading" class="text-muted">Cargando observaciones...</div>

    <!-- DataTable -->
    <DataTable
      v-else
      :data="observaciones"
      :columns="columnas"
      idField="idPlaObs"
      :acciones="false"
      :mostrarBotonNuevo="false"
      mensajeVacio="No hay observaciones para este trámite/tipo."
      @refresh="fetchData"
    >
      <!-- Botón exportar Excel en la barra de acciones -->
      <template #acciones-extra>
        <div class="col-auto">
          <button
            @click="exportToExcel"
            :disabled="observaciones.length === 0"
            class="btn btn-success btn-sm d-flex align-items-center gap-1"
            title="Exportar a Excel"
          >
            <span class="material-icons" style="font-size:16px">download</span> Excel
          </button>
        </div>
      </template>

      <!-- Celda: observación estándar — mostrar guión si vacío -->
      <template #cell-observacionDescripcion="{ row }">
        {{ row.observacionDescripcion || '-' }}
      </template>

      <!-- Celda: observación complementaria -->
      <template #cell-descripcion="{ row }">
        {{ row.descripcion || '-' }}
      </template>

      <!-- Celda: tipo -->
      <template #cell-observacionTipo="{ row }">
        {{ row.observacionTipo || '-' }}
      </template>
    </DataTable>

  </div>
</template>

<script>
import api      from "@/services/api";
import DataTable from "@/components/DataTable.vue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default {
  name: "ObservacionesPlanilla",

  components: { DataTable },

  props: {
    idTramite:       { type: [Number, String], required: true },
    observacionTipo: { type: String,           required: true },
  },

  data() {
    return {
      observaciones: [],
      loading:       false,
      errorMessage:  "",

      columnas: [
        { field: "planillaNumeroInt",       label: "Número"                    },
        { field: "planillaNombre",          label: "Nombre paciente"           },
        { field: "observacionDescripcion",  label: "Observación estándar"      },
        { field: "descripcion",             label: "Observación complementaria"},
        { field: "observacionTipo",         label: "Tipo"                      },
      ],
    };
  },

  computed: {
    tipoTexto() {
      if (this.observacionTipo === "M") return "Observaciones de Auditoría Médica";
      if (this.observacionTipo === "D") return "Observaciones de Gestión Documental";
      return this.observacionTipo;
    },
  },

  mounted() {
    this.fetchData();
  },

  watch: {
    idTramite(newVal, oldVal)       { if (newVal !== oldVal) this.fetchData(); },
    observacionTipo(newVal, oldVal) { if (newVal !== oldVal) this.fetchData(); },
  },

  methods: {
    async fetchData() {
      if (!this.idTramite) {
        this.errorMessage  = "Parámetro idTramite no definido.";
        this.observaciones = [];
        return;
      }
      this.loading      = true;
      this.errorMessage = "";
      try {
        const { data } = await api.get(
          `/vwplaobss/tramite-tipo/${this.idTramite}/${this.observacionTipo}`
        );
        this.observaciones = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("ObservacionesPlanilla - fetchData error:", err);
        this.errorMessage =
          err.response?.data?.message ||
          err.response?.statusText ||
          "Error en la petición o el servidor no responde";
        this.observaciones = [];
      } finally {
        this.loading = false;
      }
    },

    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      return [
        String(d.getDate()).padStart(2, "0"),
        String(d.getMonth() + 1).padStart(2, "0"),
        d.getFullYear(),
      ].join("-");
    },

    exportToExcel() {
      if (!this.observaciones.length) return;
      const sheetData = this.observaciones.map((o) => ({
        ID:                              o.idPlaObs,
        "ID Planilla":                   o.idPlanilla,
        "Número":                        o.planillaNumero,
        "Cédula":                        o.planillaCedula,
        "Nombre paciente":               o.planillaNombre,
        "Fecha desde":                   this.formatDate(o.planillaFechaDesde),
        "Fecha hasta":                   this.formatDate(o.planillaFechaHasta),
        "Observación estándar":          o.observacionDescripcion || "",
        "Observación complementaria":    o.descripcion            || "",
        "Tipo":                          o.observacionTipo        || "",
      }));
      const ws  = XLSX.utils.json_to_sheet(sheetData);
      const wb  = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Observaciones");
      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([out]),
        `observaciones_tramite_${this.idTramite}_atenciones.xlsx`
      );
    },
  },
};
</script>